import { IS_AUTO_TEST_HIDE, IS_FAMILY_USER } from 'reactApp/appHelpers/configHelpers';
import {
    familyDocumentsTooltip,
    familyFeatureDocuments,
    familyFeatureDocumentsFree,
    familyFeatureDocumentsPaid,
    familyFeaturePreviewAlbumsTooltip,
    familyFeaturePreviewAsideTooltip,
    familyFeaturePreviewFree,
    familyFeaturePreviewPaid,
} from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { familyLoadFailure, familyLoadSuccess } from 'reactApp/modules/family/family.actions';
import { getFamilyAlbumsFeature, getFamilyFoldersFeature } from 'reactApp/modules/family/family.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { addPromoToShowQueue, promoShown, removePromo } from 'reactApp/modules/promo/promo.module';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { dispatch } from 'reactApp/react-into-toolkit-block-wrapper/renderComponentInExternalDom';
import { getShowFamilyPageOrAlbums } from 'reactApp/sections/AlbumsPage/CustomAlbumsPage.helpers';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { put, select, take } from 'typed-redux-saga';

const ASIDE_PROMO_ID = 'family-aside-menu-tooltip';
const ALBUMS_PROMO_ID = 'family-albums-tooltip';
const DOCUMENTS_PROMO_ID = 'family-documents-tooltip';

interface PromoConfig {
    type: EPromoType.showFamilyAsideMenuTooltip | EPromoType.showFamilyAlbumsTooltip | EPromoType.showFamilyDocumentsTooltip;
    promoId: string;
    gaCategory: ECategoryGa;
    nextPromo?: PromoConfig;
}

function shouldSkipPromo(): boolean {
    const hasUserOpenedAllFamilyPages = dispatch(getShowFamilyPageOrAlbums());
    const secondClick = storeHelper.getValue('show-second-click-tip');
    return IS_AUTO_TEST_HIDE || hasUserOpenedAllFamilyPages || secondClick;
}

function createPromoConfig({ type, promoId, gaCategory, nextPromo }: PromoConfig) {
    return {
        type,
        promoId,
        onClick() {
            storeHelper.markAsShown(promoId);
            sendPaymentGa({ eventCategory: gaCategory, action: 'click', source: 'albums' });
            dispatch(removePromo(type));
        },
        onShow() {
            sendPaymentGa({ eventCategory: gaCategory, action: 'show', source: 'albums' });
            dispatch(promoShown(type));
        },
        onCrossClose() {
            sendPaymentGa({ eventCategory: gaCategory, action: 'close', source: 'albums' });
            storeHelper.markAsShown(promoId);
            dispatch(removePromo(type));
        },
        onClose() {
            dispatch(removePromo(type));
            if (nextPromo) {
                dispatch(addPromoToShowQueue(createPromoConfig(nextPromo)));
            }
        },
    };
}

export function* showFamilyAsideMenuTooltip() {
    const storage = yield* select(getCurrentStorage);
    const { isAlbums } = getStorage(storage);
    const isAsideShow = storeHelper.getValue(ASIDE_PROMO_ID);

    if (isAlbums || shouldSkipPromo() || !familyFeaturePreviewAsideTooltip || (isAsideShow && familyFeaturePreviewAsideTooltip)) {
        return;
    }

    const isAlbumsShow = storeHelper.getValue(ALBUMS_PROMO_ID);

    const nextPromo =
        !isAlbumsShow && familyFeaturePreviewAlbumsTooltip
            ? {
                  type: EPromoType.showFamilyAlbumsTooltip as const,
                  promoId: ALBUMS_PROMO_ID,
                  gaCategory: ECategoryGa.promo_button_family_albums,
              }
            : undefined;

    const promoConfig: PromoConfig = {
        type: EPromoType.showFamilyAsideMenuTooltip,
        promoId: ASIDE_PROMO_ID,
        gaCategory: ECategoryGa.sidebar_promo_button_family_section,
        nextPromo,
    };

    yield put(addPromoToShowQueue(createPromoConfig(promoConfig)));
}

export function* showFamilyAlbumsTooltip() {
    const storage = yield* select(getCurrentStorage);

    const { isAlbums } = getStorage(storage);
    const isAlbumsShow = storeHelper.getValue(ALBUMS_PROMO_ID);

    if (!isAlbums || shouldSkipPromo() || !familyFeaturePreviewAlbumsTooltip || (isAlbumsShow && familyFeaturePreviewAlbumsTooltip)) {
        return;
    }

    const isAsideShow = storeHelper.getValue(ASIDE_PROMO_ID);

    const nextPromo =
        !isAsideShow && familyFeaturePreviewAsideTooltip
            ? {
                  type: EPromoType.showFamilyAsideMenuTooltip as const,
                  promoId: ASIDE_PROMO_ID,
                  gaCategory: ECategoryGa.sidebar_promo_button_family_section,
              }
            : undefined;

    const promoConfig: PromoConfig = {
        type: EPromoType.showFamilyAlbumsTooltip,
        promoId: ALBUMS_PROMO_ID,
        gaCategory: ECategoryGa.promo_button_family_albums,
        nextPromo,
    };

    yield put(addPromoToShowQueue(createPromoConfig(promoConfig)));
}

export function* showFamilyDocumentsTooltip() {
    const storage = yield* select(getCurrentStorage);
    const {
        isHome,
        isGallery,
        isAlbums,
        isTrashBin,
        isFavorites,
        isStart,
        isAttachesStorage,
        isSharedLinks,
        isSharedAutodelete,
        isSharedIncoming,
        isDocuments,
        isFeed,
    } = getStorage(storage);
    const isPromoShown = storeHelper.getValue(DOCUMENTS_PROMO_ID);
    const isMobile = EnvironmentSelectors.isMobile();
    const isProperStorageType =
        isHome ||
        isGallery ||
        isAlbums ||
        isTrashBin ||
        isFavorites ||
        isStart ||
        isAttachesStorage ||
        isSharedLinks ||
        isSharedAutodelete ||
        isSharedIncoming ||
        isDocuments ||
        isFeed;
    const isPaid = yield select(UserSelectors.isPaidUser);
    const showToFreeUser = familyFeaturePreviewFree && !isPaid;
    const showToPaidUser = familyFeaturePreviewPaid && isPaid;
    const showToUserWithoutFamilyTariff = (showToFreeUser && familyFeatureDocumentsFree) || (showToPaidUser && familyFeatureDocumentsPaid);

    if (!familyDocumentsTooltip || isMobile || !isProperStorageType || isPromoShown || IS_AUTO_TEST_HIDE) {
        return;
    }

    let dwhData = {
        name: 'family_documents_free',
        is_family_member: IS_FAMILY_USER,
    };
    if (IS_FAMILY_USER) {
        // Дожидаемся ответа от ручки /family/get
        yield take([familyLoadSuccess, familyLoadFailure]);

        const hasFamilyFoldersFeature = yield select(getFamilyFoldersFeature);
        const hasFamilyAlbumsFeature = yield select(getFamilyAlbumsFeature);

        if (!(hasFamilyFoldersFeature && hasFamilyAlbumsFeature && familyFeatureDocuments)) {
            return;
        }

        dwhData = {
            ...dwhData,
            name: 'family_documents_paid',
        };
    } else if (!showToUserWithoutFamilyTariff) {
        return;
    }

    const promoType: EPromoType = EPromoType.showFamilyDocumentsTooltip;

    yield put(
        addPromoToShowQueue({
            type: promoType,
            promoId: DOCUMENTS_PROMO_ID,
            onClick() {
                storeHelper.markAsShown(DOCUMENTS_PROMO_ID);
                sendDwh({ eventCategory: ECategoryGa.tooltip, action: 'click', dwhData });
                dispatch(removePromo(promoType));
            },
            onShow() {
                sendDwh({ eventCategory: ECategoryGa.tooltip, action: 'show', dwhData });
                dispatch(promoShown(promoType));
            },
            onCrossClose() {
                storeHelper.markAsShown(DOCUMENTS_PROMO_ID);
                sendDwh({ eventCategory: ECategoryGa.tooltip, action: 'close', dwhData });
                dispatch(removePromo(promoType));
            },
            onClose() {
                dispatch(removePromo(promoType));
            },
        })
    );
}
